<template>
  <main
    class="w-screen h-screen sm:h-[90vh] lg:h-auto lg:max-h-screen box-border bg-white flex flex-col relative overflow-hidden p-2"
  >
    <template v-if="!isVideoReady">
      <FullScreenLoader />
    </template>

    <header
      class="w-full mb-0 text-center hidden sm:flex justify-center items-center xl:justify-start mt-2 px-4 py-4 lg:py-0"
    >
    <img v-if="chatStore?.client?.siteCustomization?.showBrandingLogo" class="w-44 h-10" :src="chatStore?.logo" alt="logo" />
    <!-- <h2 class="font-extrabold text-black text-2xl md:text-3xl">
          Glimpse
        </h2> -->
    </header>

    <section
      :class="{
        'grid-rows-1 lg:grid-cols-1': !chatStore.isChatOpen,
        'grid-rows-[2fr_1fr] lg:grid-rows-1 lg:grid-cols-[2fr_1fr]':
          chatStore.isChatOpen,
      }"
      :style="{ borderColor : chatStore?.primaryColor }"
      class="mt-2 w-full h-full grid overflow-hidden border border-[#BB80E7] rounded-2xl"
      v-if="videoUrl"
    >
      <div
        :class="{
          'h-full': !chatStore.isChatOpen,
          'h-auto': chatStore.isChatOpen,
        }"
        class="w-full flex flex-col flex-1 min-h-0 overflow-hidden"
      >
        <CustomPlayer
          ref="youtubePlayer"
          :videoUrl="videoUrl"
          :videoType="videoType"
          @pauseVideo="pauseVideo"
        />
        <div
          class="w-full flex flex-col gap-0 lg:flex-row lg:justify-between lg:items-center flex-1 relative bg-white lg:px-2"
        >
          <div class="w-full hidden lg:block"></div>
          <div
            class="w-full h-full flex flex-col justify-start lg:justify-center items-center mt-3 sm:mt-1"
          >
            <AudioRecorder
              @pauseVideo="pauseVideo"
              @resumeVideo="playVideo"
              @toggleVideo="toggleVideo"
              @speechText="processVideoQuestion"
              customIconClass="w-9 h-9 sm:w-10 sm:h-10 lg:w-5 lg:h-5 text-white "
            />
            <p class="lg:hidden">Press the microphone to speak</p>
          </div>
          <div
            class="border-b border-t-0 border-gray-200 lg:border-b-0 w-full flex justify-end items-end lg:items-center"
          >
            <button
              @click="chatStore.toggleChatUI()"
              class="hidden lg:flex items-center justify-center bg-[#3A1A55] text-white p-2 lg:p-3 rounded"
              :style="{ backgroundColor: chatStore?.primaryColor }"
            >
              <template v-if="!chatStore.isChatOpen">
                <span>Open Chat</span>
                <Icon icon="ic:round-chevron-right" width="26" height="26" />
              </template>
              <template v-else>
                <Icon icon="ic:round-chevron-left" width="26" height="26" />
              </template>
            </button>

            <button
              :class="{
                flex: chatStore.isChatOpen,
                hidden: !chatStore.isChatOpen,
              }"
              @click="chatStore.toggleChatUI()"
              :style="{ backgroundColor: chatStore?.primaryColor }"
              class="lg:hidden items-center justify-center bg-[#3A1A55] text-white text-sm lg:text-base p-2 lg:p-3 rounded-t-lg"
            >
              <span>Close Chat</span>
              <Icon icon="tabler:chevron-down" width="26" height="26" />
            </button>
          </div>
        </div>

        <div
          :class="{
            flex: !chatStore.isChatOpen,
            hidden: chatStore.isChatOpen,
          }"
          class="w-full justify-end items-start mb-4 lg:mb-0 border-t border-b-0 border-gray-200"
        >
          <button
            @click="chatStore.toggleChatUI()"
            class="lg:hidden flex items-center justify-center bg-[#3A1A55] text-white text-sm lg:text-base p-2 lg:p-3 rounded-b-lg"
            :style="{ backgroundColor: chatStore?.primaryColor }"
          >
            <span>Open Chat</span>
            <Icon icon="tabler:chevron-up" width="26" height="26" />
          </button>
        </div>
      </div>
      <div
        v-if="videoUrl"
        :class="{ hidden: !chatStore.isChatOpen }"
        class="w-full flex justify-center items-center lg:h-full min-h-0 flex-1 overflow-auto"
      >
        <ChatBotUI
          v-if="isVideoReady"
          ref="chatBotUiRef"
          @typingStarted="pauseVideo"
          @resumeVideo="playVideo"
          @newQuestion="processVideoQuestion"
          :answer="answer"
        />
        <LoaderDots v-if="!isVideoReady && chatStore.loading" />
      </div>
    </section>
  </main>
</template>

<script>
import CustomPlayer from "@/components/CustomPlayer/CustomPlayer.vue";
import ChatBotUI from "../components/ChatBotUI.vue";
import LoaderDots from "../components/LoaderDots.vue";
import AudioRecorder from "@/components/AudioRecorder.vue";
import { Icon } from "@iconify/vue";
import { getVideoWithVideoId } from "../api/processVideoRequest";
import useChatbotStore from "../store/chatbot";
import { askQuestion, generateParticipant } from "../api/userRequests";
import { Howl } from "howler";
import silentAudioFile from "../assets/audio/silence.mp3";
// import Swal from "sweetalert2";
import { isValidMongoId } from "@/utils/utils";
import FullScreenLoader from "@/components/Loader/FullScreenLoader.vue";
import defaultLogo from '../assets/glimpse-logo.png'
export default {
  components: {
    CustomPlayer,
    ChatBotUI,
    LoaderDots,
    AudioRecorder,
    Icon,
    FullScreenLoader,
  },
  data() {
    return {
      videoUrl: null, // Set empty initially
      videoType: null, // Set empty initially
      answer: "",
      logo:defaultLogo,
      isVideoReady: false, // Flag to indicate valid video is ready to play
      isUrlValid: false, // Valid URL flag
      chatStore: useChatbotStore(), // Use the store in the data option
      query: this.$route.query, // Initialize with the current query parameters

      // audio context unlock
      isAudioUnlocked: false,
      silentAudio: null,

      activeTab: "mic",
      isLargeScreen: window.innerWidth >= 1024,
    };
  },
  methods: {
    async generateUser(clientId) {
      try {
        // Generate user in background
        const participant = localStorage.getItem("participant");
        const parsed = JSON.parse(participant);
        console.log("parsed: ", parsed);

        if (!parsed?._id || parsed?.clientId !== clientId) {
          const response = await generateParticipant({
            userId: this.chatStore?.user?._id,
            clientId,
            email: parsed?.email,
          });

          if (!response?.data?.data) return;

          this.chatStore?.setUser(response?.data?.data);

          localStorage.setItem(
            "participant",
            JSON.stringify(response?.data?.data)
          );
          console.log("created new user");
          return;
        }
        console.log("using participant from local storage");
        this.chatStore?.setUser(JSON.parse(participant));
      } catch (error) {
        console.error("Generate User API call error:", error);
      }
    },
    async processVideoQuestion(question) {
      this.chatStore.setLoading(true);

      try {
        const response = await askQuestion({
          videoId: this.chatStore?.videoDetails?.id,
          question: question,
          userId: this.chatStore.user?._id,
        });
        this.answer = response?.data?.answer || "";
        // After receiving the response, set isVideoReady to true to display the video player
        if (this.answer) {
          this.isVideoReady = true;
        }
        console.log("Ask Question API call successful:", response.data);
        return true;
      } catch (error) {
        console.error("Ask Question API call error:", error);
        return false;
      } finally {
        this.chatStore.setLoading(false);
      }
    },
    speechTextProcess(text) {
      this.speakLatestAnswer = false;
      this.speechText = text;
      this.chatStore.setMessage({ sender: "user", text, type: "text" });
      this.processVideoQuestion(text);
    },

    controlVideo(action) {
      if (this.$refs.youtubePlayer) {
        this.$refs.youtubePlayer.controlVideo(action);
      }
    },
    pauseVideo() {
      console.log("pauseVideo");
      this.controlVideo("pause"); // Pause video as soon as typing starts
    },
    playVideo() {
      console.log("playVideoAfterSpeechStop");
      this.controlVideo("play");
    },
    toggleVideo(action) {
      switch (action) {
        case "play":
          console.log("playVideoAfterSpeechStop");
          this.controlVideo("play");
          break;

        case "pause":
          console.log("pauseVideo");
          this.controlVideo("pause");
          break;

        default:
          console.log("playVideoAfterSpeechStop");
          this.controlVideo("play");
          break;
      }
    },

    async getVideoById(clientId, videoId) {
      try {
        // Set loading to true and reset flags
        this.chatStore.setLoading(true);
        this.isVideoReady = false;
        this.chatStore.clearMessages();
        // Process Video
        const { data } = await getVideoWithVideoId({
          clientId: clientId,
          videoId: videoId,
        });

        if (data?.data) {
          console.log("data: ", data);

          this.videoUrl = data?.data?.videoUrl;
          this.videoType = data?.data?.videoType ?? "youtube";       
          this.chatStore.setVideoDetails(data?.data ?? null);
          this.chatStore?.setClient(data?.data?.clientId ?? null);

          console.log('chat',this.chatStore)
          // Initial API call with default question
          await this.processVideoQuestion(
            "Analyze this video and I will ask you questions later, greet the user with something you can about the video or something rather than saying sure or something for the first interaction, keep it short under 20-25 words and end it with something that motivates users to ask questions about the video"
          );
        }

        this.chatStore.setLoading(false); // Reset loading state
      } catch (error) {
        if (error?.response?.data?.status === "NOT_FOUND") {
          this.$router.push("/not-found");
        }
        if (error?.response?.data?.status === "SESSION_LIMIT_REACHED") {
          this.$router.push("/session-limit-reached");
        }
        if (error?.response?.data?.status === "FREE_QUOTA_EXCEEDED") {
          this.$router.push("/sub-not-found");
        }

        console.log("error: ", error);
      }
    },

    // Logic to fix audio context in safari
    unlockAudioContext() {
      if (this.isAudioUnlocked) return; // Prevent multiple unlock attempts

      // Play the silent audio to unlock the audio context
      this.silentAudio.play();

      this.silentAudio.once("play", () => {
        this.isAudioUnlocked = true;
        console.log("Audio context unlocked!");

        // Remove event listeners after unlocking
        this.removeEventListeners();
      });
    },
    addEventListeners() {
      window.addEventListener("click", this.unlockAudioContext);
      window.addEventListener("touchstart", this.unlockAudioContext);
      window.addEventListener("keydown", this.unlockAudioContext);
    },
    removeEventListeners() {
      window.removeEventListener("click", this.unlockAudioContext);
      window.removeEventListener("touchstart", this.unlockAudioContext);
      window.removeEventListener("keydown", this.unlockAudioContext);
    },
    setActiveTab(val) {
      this.activeTab = val;
    },
    checkScreenSize() {
      this.isLargeScreen = window.innerWidth >= 1024;
    },
  },
  async mounted() {
    // Initialize the silent audio
    this.silentAudio = new Howl({
      src: [silentAudioFile], // Replace with your silent audio file
      mute: true,
      html5: true, // Ensures that the audio is played via HTML5 audio (for large files)
      autoUnlock: true,
      autoplay: false, // Start playing programmatically
      preload: true, // Preload audio
      onunlock: function () {
        console.log("chatbox ui: howler ctx is unlocked");
      },
      onloaderror: (id, err) => {
        console.log("chatbox ui: Failed to load sound : ", id, err);
      },
      onplayerror: (id, err) => {
        console.log("chatbox ui: Failed to play sound : ", id, err);
      },
    });

    // Add event listeners for user interaction
    this.addEventListeners();

    window.addEventListener("resize", this.checkScreenSize);

    const clientId = this.$route.params.clientId;
    const videoId = this.$route.query.v;

    console.log("clientId: ", clientId);
    console.log("query: ", videoId);

    if (isValidMongoId(clientId) && isValidMongoId(videoId)) {
      await this.generateUser(clientId);
      await this.getVideoById(clientId, videoId);
    } else {
      this.$router.push("/not-found");
    }
  },
  watch: {
    "chatStore.isLastUserTry"(val) {
      if (val) {
        setTimeout(() => {
          this.chatStore.setMessage({
            sender: "bot",
            text: "Enter your email to continue using the AI Assistant.",
            type: "text",
          });
        }, 200);
      }
    },
  },
  beforeUnmount() {
    // Clean up event listeners when the component is destroyed
    this.removeEventListeners();

    window.removeEventListener("resize", this.checkScreenSize);
  },
};
</script>

<style scoped></style>
